import { useMutation } from '@apollo/client';
import { mapScheduleToTRPC } from '@risksmart-app/trpc/src/types';
import type {
  InsertChildDocumentInput,
  InsertDocumentMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertDocumentDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useInsertDocumentTRPC } from './useInsertDocumentTRPC';

export const useInsertDocument = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertDocumentGraphQL, graphqlState] = useMutation(
    InsertDocumentDocument,
    {
      update: (cache) => {
        evictField(cache, 'document');
      },
    }
  );

  const trpcMutation = useInsertDocumentTRPC();

  const insertDocument = async (
    variables: InsertChildDocumentInput
  ): Promise<InsertDocumentMutation> => {
    if (trpcEnabled) {
      const { schedule, ...rest } = variables;

      return trpcMutation.insertDocument({
        ...rest,
        ...mapScheduleToTRPC(schedule),
      });
    }

    const result = await insertDocumentGraphQL({
      variables: { object: variables },
    });
    if (!result.data) {
      throw new Error('Failed to insert document');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertDocument,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertDocument,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
